/**
 * Color utilities for Three.js plugin
 */

import type { RGBA } from '@/nodes/TypedEndpoints';
export type { RGBA };

/** Convert RGBA (0-1 range) to hex string, clamping to valid range */
export function rgbaToHex(color: RGBA): string {
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v * 255)));
  const r = clamp(color.r).toString(16).padStart(2, '0');
  const g = clamp(color.g).toString(16).padStart(2, '0');
  const b = clamp(color.b).toString(16).padStart(2, '0');
  return `#${r}${g}${b}`;
}

/** Convert hex string to RGBA */
export function hexToRgba(hex: string, alpha: number = 1): RGBA {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    return { r: 0, g: 0, b: 0, a: alpha };
  }
  return {
    r: parseInt(result[1], 16) / 255,
    g: parseInt(result[2], 16) / 255,
    b: parseInt(result[3], 16) / 255,
    a: alpha
  };
}

/** Convert HSL (0-1 range) to RGBA */
export function hslToRgba(h: number, s: number, l: number, a: number = 1): RGBA {
  let r: number, g: number, b: number;
  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }
  return { r, g, b, a };
}

/** Normalize color value to hex — handles RGBA objects, hex strings, and CSS color names */
export function colorToHex(value: any): string {
  if (typeof value === 'string') return value;
  return rgbaToHex(value);
}

/** Default colors as RGBA */
export const DEFAULT_COLORS = {
  white: { r: 1, g: 1, b: 1, a: 1 } as RGBA,
  black: { r: 0, g: 0, b: 0, a: 1 } as RGBA,
  gray: { r: 0.5, g: 0.5, b: 0.5, a: 1 } as RGBA,
  darkGray: { r: 0.067, g: 0.067, b: 0.067, a: 1 } as RGBA, // #111111
  red: { r: 1, g: 0, b: 0, a: 1 } as RGBA,
  orange: { r: 1, g: 0.5, b: 0, a: 1 } as RGBA,
  yellow: { r: 1, g: 1, b: 0, a: 1 } as RGBA,
  green: { r: 0, g: 1, b: 0, a: 1 } as RGBA,
  blue: { r: 0, g: 0, b: 1, a: 1 } as RGBA,
};
